import React, { useState, useEffect, useRef } from "react";
import Icon from "@mdi/react";
import {
    mdiChevronDown,
    mdiChevronRight,
    mdiInformationOutline,
    mdiCreditCardCheck,
    mdiCreditCardClockOutline,
    mdiCreditCardSyncOutline,
    mdiCreditCardPlusOutline,
} from "@mdi/js";
import "./ClassesHelpModal.css";

const CheckInHelpModal = ({ isOpen, onClose }) => {
    const [expandedSections, setExpandedSections] = useState([]);
    const sectionRefs = useRef({});

    useEffect(() => {
        if (!isOpen) setExpandedSections([]);
    }, [isOpen]);

    const toggleSection = sectionId => {
        const isExpanding = !expandedSections.includes(sectionId);
        setExpandedSections(prev =>
            prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
        );
        if (isExpanding) {
            setTimeout(() => {
                sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 0);
        }
    };

    if (!isOpen) return null;

    const sections = [
        {
            id: "what-is-checkin",
            title: "1. Screen layout & navigation",
            content: (
                <>
                    <p>
                        The check-in screen is your iPad-optimised attendance tool. The top bar shows a{" "}
                        <strong>week strip</strong> with day pills (Mon–Sun). Tap a day to load classes for that date.
                        Today's date is highlighted.
                    </p>
                    <p>
                        The <strong>left sidebar</strong> lists all classes for the selected day. Tap a class to load
                        its student roster on the right panel. Short courses that share a group are visually grouped
                        together.
                    </p>
                    <p>
                        <strong>Past days</strong> are shown with reduced opacity. Tapping them loads that day's
                        attendance for reviewing or correcting records. Past sessions are locked by default; see section
                        5 for details.
                    </p>
                    <p>
                        <strong>Checking in:</strong> Tap a student card to mark them as checked in. The card moves to
                        the "Checked in" section with a green indicator.
                    </p>
                    <p>
                        <strong>Undoing a check-in:</strong> Tap the undo button on a checked-in card. For{" "}
                        <strong>regular students</strong> this removes the attendance record only; their enrolment stays
                        intact. For <strong>walk-in</strong> and <strong>make-up students</strong> the undo removes
                        their enrolment entirely, since their one-night enrolment has no further purpose.
                    </p>
                </>
            ),
        },
        {
            id: "add-student",
            title: "2. Adding students & enrolment types",
            content: (
                <>
                    <p>
                        The <strong>Add Student</strong> button opens a screen to enrol a student and immediately check
                        them in for tonight's session in one step.
                    </p>
                    <p>
                        <strong>Finding existing students:</strong> Type a name or email. Search covers all{" "}
                        <strong>Dance Localé members</strong> (full platform accounts) and your school's{" "}
                        <strong>school-only records</strong>. Click <strong>Enrol</strong> on the match to enrol and
                        check them in with the selected enrolment type.
                    </p>
                    <p>
                        <strong>Registering a new student:</strong> If they cannot be found, click{" "}
                        <strong>"Register New Student at School"</strong>. Enter their name and email, optionally send
                        an invite, then confirm. They are registered, enrolled, and checked in immediately.
                    </p>
                    <p>Before confirming, select the enrolment type:</p>
                    <ul>
                        <li>
                            <strong>Regular enrolment.</strong> The student joins this class permanently and appears on
                            the roster going forward. Use for standard term enrolments.
                        </li>
                        <li>
                            <strong>Walk-in / Try-out.</strong> A one-off attendance for someone trying the class or
                            dropping in. Checked in for tonight only, does not remain on the roster. Marked with a
                            Walk-in badge in attendance history.
                        </li>
                        <li>
                            <strong>Make-up class.</strong> A one-off attendance for a student making up a missed or
                            cancelled session from another class. Checked in for tonight only, does not remain on the
                            roster. A purple <strong>Make-up</strong> pill is shown on their card in the Checked In
                            section. Make-up students always land directly in Checked In, never in Yet to Arrive.
                        </li>
                    </ul>
                </>
            ),
        },
        {
            id: "session-actions",
            title: "3. Session actions (Cancel & Instructor Override)",
            content: (
                <>
                    <p>
                        <strong>Cancel Classes</strong> cancels tonight's session(s) only. Students stay enrolled and
                        will appear again next week as normal. A <strong>Make-up owed</strong> badge is recorded in each
                        student's attendance history so you can track who is owed a make-up. You can optionally issue
                        per-student refunds during cancellation if payments have been recorded.
                    </p>
                    <p>
                        <strong>Instructor override:</strong> If a substitute taught the session, use the session
                        override option from the class actions. This updates the instructor for tonight only without
                        changing the class's default assignment.
                    </p>
                </>
            ),
        },
        {
            id: "payment-icons",
            title: "4. Payment Icons",
            content: (
                <>
                    <p>
                        Each student card shows a payment icon on the right indicating their payment status. The
                        icon reflects this class plus any unpaid records the student has elsewhere at the school, so
                        the same icon means the same thing on the Students page.
                    </p>
                    <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardCheck}
                                size={0.8}
                                color="var(--dl-color-greentick)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-greentick)" }}>Green (solid).</strong> Fully
                                paid. The student is settled across every current enrolment at the school.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardSyncOutline}
                                size={0.8}
                                color="var(--dl-color-greentick)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-greentick)" }}>Green (sync).</strong> Paid
                                externally. A payment for this class was marked received via cash, EFTPOS, bank
                                transfer, or another out-of-platform method, so it counts as paid. The sync icon just
                                notes it has not been reconciled yet. Reconcile it in Manage Finances against your
                                bank/EFTPOS transactions etc. This can be done whenever you balance your books.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardClockOutline}
                                size={0.8}
                                color="var(--dl-color-purple-900)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-purple-900)" }}>Purple.</strong> Payment plan
                                in progress. The student is on an instalment plan; at least one instalment is paid
                                and more are still expected.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardPlusOutline}
                                size={0.8}
                                color="var(--dl-color-blue-default)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-blue-default)" }}>Blue.</strong> Awaiting
                                price package. The enrolment is confirmed but no price package has been assigned yet,
                                so no payment can be collected until you assign one.
                            </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                            <Icon
                                path={mdiCreditCardClockOutline}
                                size={0.8}
                                color="var(--dl-color-needs-action)"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                            />
                            <span>
                                <strong style={{ color: "var(--dl-color-needs-action)" }}>Orange.</strong> Payment
                                outstanding. Either no payment has been recorded for this class yet, or the student
                                has an unpaid balance from another class at the school.
                            </span>
                        </div>
                    </div>
                    <p style={{ marginTop: "8px" }}>
                        Tap the icon to open the payment modal and collect or record a payment.
                    </p>
                </>
            ),
        },
        {
            id: "past-day-locking",
            title: "5. Past Day Sessions & Locking",
            content: (
                <>
                    <p>
                        When you navigate to a past day, the session is <strong>locked by default</strong>. A grey
                        banner appears above the student list indicating the session is locked. Check-in and undo
                        buttons are dimmed and inactive while locked.
                    </p>
                    <p>This prevents accidental changes to finalised attendance records.</p>
                    <p>
                        <strong>Unlocking a session:</strong> Click the <strong>Unlock to Edit</strong> button in the
                        class header row. Confirm the prompt. The banner disappears and check-in/undo buttons become
                        active. You can then correct attendance as needed. The unlocked state is saved, so if you
                        navigate away and return the session remains unlocked.
                    </p>
                    <p>
                        <strong>Locking a session:</strong> Once editing is complete, click{" "}
                        <strong>Lock Session</strong> to mark the session as final. The locked banner reappears and the
                        session is protected again.
                    </p>
                    <p>
                        <strong>Short courses</strong> are exempt from locking and their check-in always remains
                        editable regardless of date.
                    </p>
                </>
            ),
        },
        {
            id: "student-photos",
            title: "6. Student Photos",
            content: (
                <>
                    <p>
                        Each student card shows a circular avatar on the left. For students with no photo, their
                        initials are shown as a placeholder.
                    </p>
                    <p>
                        <strong>To add a photo:</strong> Tap the circular avatar on any student card, select an image
                        from your device, and tap <strong>Save</strong> when you are happy with the preview.
                    </p>
                    <p>
                        <strong>To change a photo:</strong> Tap the avatar again and select a replacement image.
                    </p>
                    <p>
                        <strong>To remove a photo:</strong> Tap the avatar, then tap <strong>Remove Photo</strong>.
                    </p>
                    <p>
                        If a student has a <strong>Dance Localé</strong> account linked to their enrolment, their
                        platform profile photo is used automatically as a fallback. If you cannot identify the student
                        from their account photo, you can upload a school-only photo here instead. A school-uploaded
                        photo always takes priority over their personal account photo.
                    </p>
                </>
            ),
        },
    ];

    return (
        <div className="classes-help-overlay" onClick={onClose}>
            <div className="classes-help-modal" onClick={e => e.stopPropagation()}>
                <div className="classes-help-header">
                    <div className="classes-help-header-content">
                        <Icon path={mdiInformationOutline} size={1.2} color="var(--dl-color-purple-default)" />
                        <h2>Check-In Help</h2>
                    </div>
                    <button className="classes-help-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="classes-help-body">
                    {sections.map(section => (
                        <div key={section.id} className="help-accordion-section" ref={el => sectionRefs.current[section.id] = el}>
                            <button className="help-accordion-header" onClick={() => toggleSection(section.id)}>
                                <Icon
                                    path={expandedSections.includes(section.id) ? mdiChevronDown : mdiChevronRight}
                                    size={0.8}
                                    color="var(--dl-color-purple-default)"
                                />
                                <span>{section.title}</span>
                            </button>
                            {expandedSections.includes(section.id) && (
                                <div className="help-accordion-content">{section.content}</div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="classes-help-footer">
                    <button className="classes-help-btn-secondary" onClick={onClose}>
                        Got it!
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CheckInHelpModal;
